import { AxiosInstance, AxiosResponse } from 'axios';
import { ListWithTotals, UpdateUserDto, UserDto, StreamChatDto, UserFilter, FileDto, UserFilesFilter, OneUserFilters, HostSitemap, PaginationFilter, NewsletterDto, UserNotificationSettingDto, UpdateUserNotificationSettingDto } from '@sweatpals/shared';

export class UsersService {
	private readonly baseRoute = 'users';

	constructor(private readonly httpClient: AxiosInstance) {}

	public async findAll(filter: UserFilter): Promise<AxiosResponse<ListWithTotals<UserDto>>> {
		return this.httpClient.get<ListWithTotals<UserDto>>(`${this.baseRoute}/`, {
			params: filter,
		});
	}

	public async findAllPublic(filter: UserFilter): Promise<AxiosResponse<ListWithTotals<UserDto>>> {
		return this.httpClient.get<ListWithTotals<UserDto>>(`${this.baseRoute}/public`, {
			params: filter,
		});
	}

	public async getUsersCountByFilter(filter: UserFilter): Promise<AxiosResponse<number>> {
		return this.httpClient.post<number>(`${this.baseRoute}/users-count`, filter);
	}

	public async getPublicUsersCount(filter: UserFilter): Promise<AxiosResponse<number>> {
		return this.httpClient.post<number>(`${this.baseRoute}/users-count/public`, filter);
	}

	public async getCurrent(): Promise<AxiosResponse<UserDto>> {
		return this.httpClient.get<UserDto>(`${this.baseRoute}/current`);
	}

	public async getByUsername(username: string, filters?: OneUserFilters): Promise<AxiosResponse<UserDto>> {
		return this.httpClient.get<UserDto>(`${this.baseRoute}/username/${username}`, { params: filters });
	}

	public async verifyUserNameCanBeUsed(username: string): Promise<AxiosResponse<true>> {
		return this.httpClient.get<true>(`${this.baseRoute}/${username}/availability`);
	}

	public async getById(userId: string, filters?: OneUserFilters): Promise<AxiosResponse<UserDto>> {
		return this.httpClient.get<UserDto>(`${this.baseRoute}/${userId}`, { params: filters });
	}

	public async findPublicIdByUsername(userName: string): Promise<AxiosResponse<string | null>> {
		return this.httpClient.get<string | null>(`${this.baseRoute}/public/username/${userName}/id`);
	}

	public async findPublicUserNameById(id: string): Promise<AxiosResponse<string | null>> {
		return this.httpClient.get<string | null>(`${this.baseRoute}/public/${id}/username`);
	}

	public async getByIdPublic(userId: string, filters?: OneUserFilters): Promise<AxiosResponse<UserDto>> {
		return this.httpClient.get<UserDto>(`${this.baseRoute}/public/${userId}`, { params: filters });
	}

	public async getUserProfileChat(userId: string): Promise<AxiosResponse<StreamChatDto>> {
		return this.httpClient.get<StreamChatDto>(`${this.baseRoute}/${userId}/profile-chat`);
	}

	public async getInviteCodeByUserId(userId: string): Promise<AxiosResponse<string>> {
		return this.httpClient.get<string>(`${this.baseRoute}/${userId}/profile-chat/invitation-code`);
	}

	public async getUserByInvitationCode(invitationCode: string): Promise<AxiosResponse<UserDto>> {
		return this.httpClient.get<UserDto>(`${this.baseRoute}/user/by-invitation-code/${invitationCode}`);
	}

	public async update(userId: string, body: UpdateUserDto): Promise<AxiosResponse<UserDto>> {
		return this.httpClient.put<UserDto>(`${this.baseRoute}/${userId}`, body);
	}

	public async createWithData(body: UpdateUserDto): Promise<AxiosResponse<UserDto>> {
		return this.httpClient.post<UserDto>(`${this.baseRoute}`, body);
	}

	public async updateCurrent(body: UpdateUserDto): Promise<AxiosResponse<UserDto>> {
		return this.httpClient.put<UserDto>(`${this.baseRoute}/`, body);
	}

	public async updateUserVerifyStatus(userId: string, verifyStatus: boolean): Promise<AxiosResponse<boolean>> {
		return this.httpClient.put<boolean>(`${this.baseRoute}/${userId}/verify-status`, { verifyStatus });
	}

	public async follow(userId: string): Promise<AxiosResponse<void>> {
		return this.httpClient.post<void>(`${this.baseRoute}/follow/${userId}`);
	}

	public async restore(userId: string): Promise<AxiosResponse<void>> {
		return this.httpClient.post<void>(`${this.baseRoute}/restore/${userId}`);
	}

	public async delete(userId: string): Promise<AxiosResponse<void>> {
		return this.httpClient.delete<void>(`${this.baseRoute}/${userId}`);
	}

	public async deleteFull(userId: string): Promise<AxiosResponse<void>> {
		return this.httpClient.delete<void>(`${this.baseRoute}/full/${userId}`);
	}

	public async getUserFiles(filter: UserFilesFilter): Promise<AxiosResponse<ListWithTotals<FileDto>>> {
		return this.httpClient.get<ListWithTotals<FileDto>>(`${this.baseRoute}/files/search`, {
			params: filter,
		});
	}

	public async resetStripeAccount(userId: string): Promise<AxiosResponse<void>> {
		return this.httpClient.put<void>(`${this.baseRoute}/${userId}/reset-stripe-account`);
	}

	public async getUsersSitemap(filter: UserFilter): Promise<AxiosResponse<HostSitemap[]>> {
		return this.httpClient.post<HostSitemap[]>(`${this.baseRoute}/sitemap`, filter);
	}

	public async getUserCommunityProfiles(userId: string): Promise<AxiosResponse<ListWithTotals<UserDto>>> {
		return this.httpClient.get(`/community-profiles`, {
			params: {
				userId,
			},
		});
	}

	public async getUserProfileChatInvitationCodePublic(userId: string): Promise<AxiosResponse<string | null>> {
		return this.httpClient.get<string | null>(`${this.baseRoute}/${userId}/profile-chat/invitation-code`);
	}

	public async addToNewsletter(email: string): Promise<AxiosResponse<void>> {
		return this.httpClient.post<void>(`${this.baseRoute}/add-to-newsletter`, { email });
	}

	public async getNewsletterEmails(filter: PaginationFilter): Promise<AxiosResponse<ListWithTotals<NewsletterDto>>> {
		return this.httpClient.get<ListWithTotals<NewsletterDto>>(`${this.baseRoute}/newsletter/emails`, { params: filter });
	}

	public async adminGetNotificationSettings(userId: string): Promise<AxiosResponse<UserNotificationSettingDto[]>> {
		return this.httpClient.get<UserNotificationSettingDto[]>(`notifications/admin/settings/user/${userId}`);
	}

	public async adminUpdateNotificationSettings(userId: string, settings: UpdateUserNotificationSettingDto[]): Promise<AxiosResponse<boolean>> {
		return this.httpClient.put<boolean>(`notifications/admin/update-settings/user/${userId}`, settings);
	}
}

export default UsersService;
